/**
 * PaymentOrderDetailDialog — el detall d'un enllaç de pagament obert (`plans/stripe-payment-links.md` F5).
 *
 * El popover només porta el resum; aquí hi ha l'enllaç, les línies (una per reserva), els
 * cobraments i les accions: marcar com enviat, cancel·lar, crear-ne un de nou (que deixa aquest
 * sense valor) o retirar-lo. Mai es canvia l'import d'un enllaç ja creat (§3.4).
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Copy } from 'lucide-react';
import type { PaymentOrderSentVia, PaymentOrderView } from '@guilleumes/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  CANCEL_REASON_MAX,
  ORDER_STATUS_LABELS,
  SENT_VIAS,
  SENT_VIA_LABELS,
  MARK_SENT_TITLE,
  formatCents,
  PAST_DUE_TEXT,
  checkIssueText,
  formatDueAt,
  orderCheckLineTexts,
  orderCheckProblem,
  orderCheckSentence,
  orderReviewReasons,
  reviewReasonLabel,
  sentViaLabel,
  cancelOrderSchema,
  type CancelOrderFormValues,
  chargeStatusLabel,
  methodLabel,
} from '@/lib/payment-orders';
import { PAYMENT_DIALOG_Z } from './payment-link-utils';

export interface PaymentOrderDetailProps {
  order: PaymentOrderView;
  pending: boolean;
  onCopy: (url: string) => void;
  onMarkSent: (via: PaymentOrderSentVia) => void;
  onCancel: (reason: string) => void;
  /** «Crea'n un de nou»: obre el diàleg de crear; en crear-lo, aquest deixa de valer. */
  onReplace: () => void;
  /** «Retira'l»: el deixa substituït sense crear-ne cap. */
  onSupersede: () => void;
  /** Un enllaç caducat ofereix «Crea'n un de nou» (si encara hi ha res a cobrar). Per defecte, sí. */
  canReplace?: boolean;
}

/** El cos del detall, sense el `Dialog` (els tests el pinten directament). */
export function PaymentOrderDetail({
  order,
  pending,
  onCopy,
  onMarkSent,
  onCancel,
  onReplace,
  onSupersede,
  canReplace = true,
}: PaymentOrderDetailProps) {
  const [confirming, setConfirming] = useState<'cancel' | 'supersede' | null>(null);
  const form = useForm<CancelOrderFormValues>({
    resolver: zodResolver(cancelOrderSchema),
    defaultValues: { reason: '' },
  });
  const isOpen = order.status === 'OPEN';
  const check = orderCheckProblem(order);
  const checkLines = check ? orderCheckLineTexts(check) : [];
  const reviewReasons = orderReviewReasons(order);
  /** Oberta i amb `check`: el «per revisar» desat sense cap altre motiu ja el contesta la comprovació. */
  const checkAnswers = isOpen && order.check != null;

  return (
    <div className="flex flex-col gap-3 text-sm" data-testid="payment-order-detail">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-xl font-bold">{formatCents(order.amountCents)}</span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">
          {ORDER_STATUS_LABELS[order.status] ?? order.status}
        </span>
        {order.testMode && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-900">
            PROVA
          </span>
        )}
      </div>
      <p className="text-gray-700">
        {order.dueAt
          ? `${isOpen ? 'Es pot pagar' : 'Es podia pagar'} fins al ${formatDueAt(order.dueAt)}.`
          : 'Sense data màxima.'}{' '}
        {order.sentAt
          ? `Enviat el ${formatDueAt(order.sentAt)}${order.sentVia ? ` (${sentViaLabel(order.sentVia)})` : ''}.`
          : 'Encara no consta enviat.'}
      </p>

      {order.pastDue && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{PAST_DUE_TEXT}</span>
        </div>
      )}

      {/* La comprovació d'ARA contra les reserves (§3.4): mana per sobre del motiu desat. */}
      {check && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800"
          data-testid="payment-order-check"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="flex min-w-0 flex-col gap-1">
            <p className="font-semibold">{orderCheckSentence(check)}</p>
            {check.issues.length > 0 && (
              <ul className="list-disc pl-4">
                {check.issues.map((issue) => (
                  <li key={issue}>{checkIssueText(issue)}</li>
                ))}
              </ul>
            )}
            {checkLines.length > 0 && (
              <ul className="flex flex-col gap-0.5 text-xs">
                {checkLines.map((line) => (
                  <li key={line.lineId} className="break-words">
                    {line.text}
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs">
              L'import d'un enllaç creat no es canvia: se'n crea un de nou i aquest deixa de valer.
            </p>
            <div>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={pending}
                onClick={onReplace}
                aria-label="Crea un enllaç nou que substitueixi aquest"
              >
                Crea'n un de nou
              </Button>
            </div>
          </div>
        </div>
      )}

      {reviewReasons.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800"
          data-testid="payment-order-review-reasons"
        >
          <p className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            Per revisar
          </p>
          <ul className="mt-1 list-disc pl-8">
            {reviewReasons.map((code) => (
              <li key={code} className="break-words">
                {reviewReasonLabel(code)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {reviewReasons.length === 0 && order.needsReviewAt && !checkAnswers && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Per revisar: sense motiu escrit.</span>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Input readOnly value={order.payUrl} aria-label="Enllaç de pagament" className="h-9 flex-1 text-xs" />
        <Button type="button" variant="outline" onClick={() => onCopy(order.payUrl)} aria-label="Copiar enllaç">
          <Copy className="mr-2 h-4 w-4" />
          Copiar
        </Button>
      </div>

      <div>
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Què es cobra</p>
        <ul className="flex flex-col gap-1">
          {order.lines.map((line) => (
            <li key={line.id} className="flex justify-between gap-2">
              <span className="min-w-0 break-words">{line.description || `Reserva ${line.beds24Id}`}</span>
              <span className="shrink-0 font-medium">{formatCents(line.amountCents)}</span>
            </li>
          ))}
        </ul>
      </div>

      {order.charges.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Cobraments</p>
          <ul className="flex flex-col gap-1">
            {order.charges.map((charge) => (
              <li key={charge.id} className="flex justify-between gap-2 text-xs">
                <span>
                  {chargeStatusLabel(charge.status)}
                  {charge.methodType ? ` · ${methodLabel(charge.methodType)}` : ''}
                  {charge.paidAt ? ` · ${formatDueAt(charge.paidAt)}` : ''}
                  {charge.refundedCents > 0 ? ` · retornat ${formatCents(charge.refundedCents)}` : ''}
                </span>
                <span className="shrink-0 font-medium">
                  {formatCents(charge.amountCents)}
                  {charge.netCents != null ? ` (net ${formatCents(charge.netCents)})` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {order.status === 'EXPIRED' && canReplace && (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <span className="text-xs text-gray-600">
            Aquest enllaç ha caducat{order.expiredAt ? ` el ${formatDueAt(order.expiredAt)}` : ''}.
          </span>
          <Button type="button" size="sm" disabled={pending} onClick={onReplace} aria-label="Crea'n un de nou">
            Crea'n un de nou
          </Button>
        </div>
      )}

      {isOpen && (
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Marca com enviat">
            <span className="text-xs font-medium text-gray-600">
              {order.sentAt ? 'Torna a marcar com enviat per:' : 'Marca com enviat per:'}
            </span>
            {SENT_VIAS.map((via) => (
              <Button
                key={via}
                type="button"
                size="sm"
                variant="outline"
                disabled={pending}
                onClick={() => onMarkSent(via)}
                title={MARK_SENT_TITLE}
                aria-label={`Marca com enviat per ${SENT_VIA_LABELS[via]}`}
              >
                {SENT_VIA_LABELS[via]}
              </Button>
            ))}
          </div>

          {confirming === null ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" disabled={pending} onClick={onReplace} aria-label="Crea'n un de nou">
                Crea'n un de nou
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                disabled={pending}
                onClick={() => setConfirming('cancel')}
                aria-label="Cancel·la l'enllaç"
              >
                Cancel·la l'enllaç
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={pending}
                onClick={() => setConfirming('supersede')}
                title="Deixa'l sense valor perquè l'import o les dates han canviat, sense crear-ne cap"
                aria-label="Retira'l"
              >
                Retira'l
              </Button>
            </div>
          ) : confirming === 'cancel' ? (
            <form
              className="flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 p-3"
              onSubmit={form.handleSubmit((values) => onCancel(values.reason))}
            >
              <Label htmlFor="payment-order-cancel-reason">
                Cancel·lar l'enllaç? L'hoste ja no podrà pagar-lo. Motiu (opcional)
              </Label>
              <Input
                id="payment-order-cancel-reason"
                maxLength={CANCEL_REASON_MAX}
                {...form.register('reason')}
              />
              {form.formState.errors.reason && (
                <p className="text-xs text-red-600">{form.formState.errors.reason.message}</p>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="ghost" disabled={pending} onClick={() => setConfirming(null)}>
                  No
                </Button>
                <Button type="submit" size="sm" variant="destructive" disabled={pending}>
                  Sí, cancel·la'l
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p>
                Retirar l'enllaç? Queda «substituït» i l'hoste ja no podrà pagar-lo. Si en vols un
                amb l'import bo, fes servir «Crea'n un de nou».
              </p>
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="ghost" disabled={pending} onClick={() => setConfirming(null)}>
                  No
                </Button>
                <Button type="button" size="sm" disabled={pending} onClick={onSupersede}>
                  Sí, retira'l
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export interface PaymentOrderDetailDialogProps extends PaymentOrderDetailProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PaymentOrderDetailDialog({ open, onOpenChange, ...detail }: PaymentOrderDetailDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-h-[90vh] overflow-y-auto sm:max-w-lg', PAYMENT_DIALOG_Z)}>
        <DialogHeader>
          <DialogTitle>Enllaç de pagament</DialogTitle>
          <DialogDescription>
            L'import d'un enllaç creat no es canvia: si no és bo, se'n crea un de nou.
          </DialogDescription>
        </DialogHeader>
        {/* Es torna a muntar en obrir: cap confirmació a mitges d'una vegada anterior. */}
        {open && <PaymentOrderDetail {...detail} />}
      </DialogContent>
    </Dialog>
  );
}

export default PaymentOrderDetailDialog;
